import { type ComponentProps, useEffect, useState } from 'react';

import { dateTimeAsMoment } from '@grafana/data';
import { reportInteraction } from '@grafana/runtime';
import {
  useFlagGrafanaAnnouncementBannerDismissible,
  useFlagGrafanaVisualDesignRefresh,
  useUserStorage,
} from '@grafana/runtime/internal';
import { Box } from '@grafana/ui';
import { contextSrv } from 'app/core/services/context_srv';

import { type Spec } from '../api/clients/banners/v0alpha1';

import { Banner } from './Banner';
import { useBanner } from './hooks';

const DISMISSED_KEY = 'dismissed';

export function TopBanner() {
  const [banner] = useBanner();
  const dismissibleEnabled = useFlagGrafanaAnnouncementBannerDismissible();
  if (!banner?.spec?.enabled) {
    return null;
  }

  const { message, variant, startTime, endTime, visibility, dismissible } = banner.spec;
  // A missing bound is open-ended. moment's isBetween would read it as "now", taken a moment after
  // `now` below, so the banner randomly disappears whenever the clock ticks in between.
  const now = dateTimeAsMoment();
  const isInDateRange = (!startTime || !now.isBefore(startTime)) && (!endTime || !now.isAfter(endTime));
  const userCanView = visibility === 'everyone' || (visibility === 'authenticated' && contextSrv.user.isSignedIn);
  if (!isInDateRange || !userCanView) {
    return null;
  }

  if (!dismissibleEnabled || !dismissible) {
    return <PageBanner message={message} variant={variant} />;
  }

  // Any update to the banner bumps its resourceVersion, so an updated banner shows again
  const revision = `${banner.metadata?.name}/${banner.metadata?.resourceVersion}`;
  return (
    <DismissibleBanner
      revision={revision}
      message={message}
      variant={variant}
      startTime={startTime}
      endTime={endTime}
    />
  );
}

type DismissibleBannerProps = Pick<Spec, 'message' | 'variant' | 'startTime' | 'endTime'> & { revision: string };

function DismissibleBanner({ revision, message, variant, startTime, endTime }: DismissibleBannerProps) {
  const storage = useUserStorage('announcement-banner');
  // Hidden until the stored dismissal has loaded, so a dismissed banner doesn't flash on page load
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    storage.getItem(DISMISSED_KEY).then((stored) => setDismissed(stored === revision));
  }, [storage, revision]);

  if (dismissed) {
    return null;
  }

  const onDismiss = () => {
    setDismissed(true);
    storage.setItem(DISMISSED_KEY, revision);
    // A banner with no start or end time sends it as undefined, which is left out of the event
    reportInteraction('grafana_banner_dismissed', { variant, startTime, endTime });
  };

  return <PageBanner message={message} variant={variant} onDismiss={onDismiss} />;
}

// The refreshed page is inset, so inset the banner too to line their edges up
function PageBanner(props: ComponentProps<typeof Banner>) {
  const inset = useFlagGrafanaVisualDesignRefresh() ? 0.5 : 0;
  return (
    <Box marginX={inset} marginBottom={inset}>
      <Banner {...props} />
    </Box>
  );
}
